import { MUSIC_COLLECTION } from './collections';
import { SINGLE_QUERY_PAGE_SIZE } from './queries';
import type {
  DropTokensMode,
  SimilarityField,
  SimilarityWeights,
} from './similarity-weights';
import {
  DEFAULT_FIELD_ORDER,
  DEFAULT_FIELDS_ENABLED,
  DEFAULT_SIMILARITY_WEIGHTS,
  generateQueryByWeights,
} from './similarity-weights';
import type { TracksWithEngagementDocument } from './tracks-with-engagement';
import type { SortOption } from './typesense-sort';
import { getSortBy } from './typesense-sort';

// Similarity search field configuration (decided on DEV-6928, 2026-09-30).
// Every keyword sits in exactly one weight field. Not `keywords`: Typesense
// scores a track by its best-matching field, so it would lift weight-1 words
// above weight-2 ones. Not genre/mood/theme: they are derived from keywords.
export const SIMILARITY_QUERY_BY = 'keywords_w3,keywords_w2,keywords_w1';

export const SIMILARITY_QUERY_BY_WEIGHTS = generateQueryByWeights(
  DEFAULT_SIMILARITY_WEIGHTS
);

// Map field identifiers to track properties
const getFieldValues = (
  track: TracksWithEngagementDocument,
  field: SimilarityField
): string[] => {
  switch (field) {
    case 'keywordsW3':
      return track.keywords_w3 ?? [];
    case 'keywordsW2':
      return track.keywords_w2 ?? [];
    case 'keywordsW1':
      return track.keywords_w1 ?? [];
    default:
      return [];
  }
};

/**
 * Extracts the weighted keywords from a reference track and combines them into a search query string
 * The keywords from all three weight tiers are combined into a comma-separated query
 * Field order can be customized for debugging - affects which tokens get dropped first
 * Fields can be disabled to exclude them from the search entirely
 */
export const extractStylesFromTrack = (
  track: TracksWithEngagementDocument,
  fieldOrder: SimilarityField[] = DEFAULT_FIELD_ORDER,
  fieldsEnabled: Record<SimilarityField, boolean> = DEFAULT_FIELDS_ENABLED
): string => {
  // Filter to only include enabled fields, then get values
  const allStyles = fieldOrder
    .filter((field) => fieldsEnabled[field])
    .flatMap((field) => getFieldValues(track, field));

  // Filter out empty strings and join with commas for Typesense multi-term search
  return allStyles.filter(Boolean).join(', ');
};

/**
 * Generates search queries for similar tracks based on a reference track's styles
 * Follows the same pattern as getMusicSearches in queries.ts
 */
export const getSimilaritySearches = ({
  referenceTrack,
  excludeTrackId,
  sort = 'relevancy',
  page = 1,
  sortScoreVersion = 'v1',
  isSubscribedUser = false,
  weightOverrides,
  fieldOrderOverride,
  dropTokensModeOverride,
  fieldsEnabledOverride,
}: {
  referenceTrack: TracksWithEngagementDocument;
  excludeTrackId?: string;
  sort: SortOption;
  page?: number;
  sortScoreVersion?: 'v1' | 'v2' | 'v3' | 'v4' | 'v5';
  isSubscribedUser?: boolean;
  /** Optional weight overrides for admin debugging */
  weightOverrides?: SimilarityWeights | null;
  /** Optional field order override for admin debugging */
  fieldOrderOverride?: SimilarityField[] | null;
  /** Optional drop tokens mode override for admin debugging */
  dropTokensModeOverride?: DropTokensMode | null;
  /** Optional fields enabled override for admin debugging */
  fieldsEnabledOverride?: Record<SimilarityField, boolean> | null;
}) => {
  // Extract styles from reference track to use as search query
  // Use field order and fields enabled overrides if provided for debugging
  const styleQuery = extractStylesFromTrack(
    referenceTrack,
    fieldOrderOverride ?? DEFAULT_FIELD_ORDER,
    fieldsEnabledOverride ?? DEFAULT_FIELDS_ENABLED
  );

  // Use weight overrides if provided, otherwise use default weights
  const queryByWeights = weightOverrides
    ? generateQueryByWeights(weightOverrides)
    : SIMILARITY_QUERY_BY_WEIGHTS;

  // Use drop tokens mode override if provided
  const dropTokensMode = dropTokensModeOverride ?? 'right_to_left';

  // Build filter to exclude the reference track from results
  const excludeFilter = excludeTrackId
    ? `track_id:!=${excludeTrackId}`
    : undefined;

  const collection = MUSIC_COLLECTION;

  // Determine if we have search terms (styles) for text matching
  const hasSearchTerms = styleQuery.length > 0;

  // Similar music is ordered purely by match score - no free/premium interspersing
  // (DEV-6895: interspersing pushed free tracks up and made matches feel less accurate)
  return [
    {
      q: styleQuery || '*',
      query_by: SIMILARITY_QUERY_BY,
      query_by_weights: queryByWeights,
      collection,
      per_page: SINGLE_QUERY_PAGE_SIZE,
      filter_by: excludeFilter,
      sort_by: getSortBy(
        sort,
        isSubscribedUser,
        false,
        collection,
        sortScoreVersion,
        hasSearchTerms
      ),
      // Drop tokens progressively if results are below threshold to find more matches
      drop_tokens_threshold: SINGLE_QUERY_PAGE_SIZE,
      drop_tokens_mode: dropTokensMode,
      page,
      exclude_fields: 'id',
    },
  ];
};
